import React, { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { RigidBody } from "@react-three/rapier";
import * as THREE from "three";
import { getRockTexture, getWaterTexture } from "./textures";
import { playerPosition } from "./controls";
import { startWaterLoop, setWaterVolume } from "./audio";

interface WaterfallProps {
  position: [number, number, number];
}

const AUDIBLE_RADIUS = 16;

const Waterfall: React.FC<WaterfallProps> = ({ position }) => {
  const rock = getRockTexture();
  const poolRef = useRef<THREE.Mesh>(null);
  const posVec = useMemo(() => new THREE.Vector3(...position), [position]);

  const fallTexture = useMemo(() => {
    const tex = getWaterTexture().clone();
    tex.needsUpdate = true;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(1, 3);
    return tex;
  }, []);

  useEffect(() => {
    startWaterLoop();
  }, []);

  useFrame((_, delta) => {
    fallTexture.offset.y += delta * 0.9;
    if (poolRef.current) {
      const material = poolRef.current.material as THREE.MeshStandardMaterial;
      material.opacity = 0.75 + Math.sin(Date.now() * 0.001) * 0.05;
    }

    const dist = playerPosition.distanceTo(posVec);
    setWaterVolume(1 - Math.min(dist / AUDIBLE_RADIUS, 1));
  });

  return (
    <group position={position}>
      {/* Rocky cliff ledge */}
      <RigidBody type="fixed" colliders="cuboid">
        <mesh position={[0, 1.6, -0.6]} castShadow receiveShadow>
          <boxGeometry args={[4.5, 3.2, 2.2]} />
          <meshStandardMaterial map={rock} roughness={1} flatShading />
        </mesh>
      </RigidBody>
      <mesh position={[-1.2, 3.1, -0.3]} rotation={[0.15, 0.3, 0.1]} castShadow>
        <dodecahedronGeometry args={[0.9, 0]} />
        <meshStandardMaterial map={rock} roughness={1} flatShading />
      </mesh>
      <mesh position={[1.3, 3.0, -0.5]} rotation={[0.1, -0.4, -0.1]} castShadow>
        <dodecahedronGeometry args={[0.7, 0]} />
        <meshStandardMaterial map={rock} roughness={1} flatShading />
      </mesh>

      {/* Falling water */}
      <mesh position={[0, 1.6, 0.55]}>
        <planeGeometry args={[1.6, 3.2]} />
        <meshStandardMaterial
          map={fallTexture}
          transparent
          opacity={0.85}
          emissive="#3fa9d8"
          emissiveIntensity={0.25}
          roughness={0.2}
        />
      </mesh>

      {/* Splash mist */}
      <mesh position={[0, 0.15, 1.1]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[1.1, 16]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.18} />
      </mesh>

      {/* Pool */}
      <mesh ref={poolRef} position={[0, 0.06, 2.2]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[2.6, 20]} />
        <meshStandardMaterial color="#3fa9d8" transparent opacity={0.8} roughness={0.1} metalness={0.1} />
      </mesh>

      {/* Pool-edge rocks */}
      {[
        [-2.1, 0.15, 2.6, 0.4],
        [2.0, 0.15, 2.0, 0.35],
        [0.6, 0.15, 3.6, 0.3],
        [-1.0, 0.15, 3.8, 0.32],
      ].map(([x, y, z, s], i) => (
        <mesh key={i} position={[x, y, z]} scale={s} rotation={[0.2 * i, 0.5 * i, 0]} castShadow>
          <dodecahedronGeometry args={[0.6, 0]} />
          <meshStandardMaterial map={rock} roughness={1} flatShading />
        </mesh>
      ))}
    </group>
  );
};

export default Waterfall;
